import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { AppShell } from '@/components/AppShell';
import { EmptyState, ActionLink } from '@/components/visual/PagePrimitives';
import { LocaleLink } from '@/components/LocaleLink';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { GitCompareArrows, Calculator, Loader2, Layers, X, Share2, FileText, Save } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/hooks/useLocale';
import { useSeoMeta } from '@/hooks/usePageTitle';
import { readDrafts, removeDraft } from '@/lib/scenarioDrafts';
import { ScenarioSetCard } from '@/components/scenarios/ScenarioSetCard';
import { ScenarioComparison } from '@/components/scenarios/ScenarioComparison';
import { tcoOverridesOf } from '@/lib/assumptions';
import { DecisionContextCard } from '@/components/scenarios/DecisionContextCard';
import { ShareComparisonDialog } from '@/components/scenarios/ShareComparisonDialog';
import { generateBoardPackPDF } from '@/lib/boardPackPdf';
import { trackProductEvent } from '@/lib/analytics';
import logger from '@/lib/logger';

const API = '/api';
const MAX_COMPARE = 3;

export default function ScenariosPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { localePath } = useLocale();
  useSeoMeta(t('scenarios.metaTitle'), t('scenarios.metaDescription'));

  const [sets, setSets] = useState([]);
  const [shares, setShares] = useState([]);
  const [drafts, setDrafts] = useState(() => readDrafts());
  const [loading, setLoading] = useState(Boolean(user));
  const [selected, setSelected] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [shareSnapshot, setShareSnapshot] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const shareTriggerRef = useRef(null);
  const [savingSet, setSavingSet] = useState(false);
  const canManage = Boolean(user) && !user?.is_demo;

  const fetchSets = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const [groups, links] = await Promise.all([
        axios.get(`${API}/scenarios`, { withCredentials: true }),
        axios.get(`${API}/scenarios/shares`, { withCredentials: true }),
      ]);
      setSets(groups.data);
      setShares(links.data);
    } catch (err) {
      logger.warn('[ScenariosPage] load failed:', err?.message || err);
      toast.error(t('scenarios.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user, t]);

  useEffect(() => { fetchSets(); }, [fetchSets]);

  const resetComparison = () => {
    setComparison(null);
    setShareSnapshot(null);
  };

  const toggle = (id) => {
    resetComparison();
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(s => s !== id);
      if (prev.length >= MAX_COMPARE) {
        toast.info(t('scenarios.compareLimit'));
        return prev;
      }
      return [...prev, id];
    });
  };

  const resolve = useCallback((id) => {
    const draft = drafts.find(d => d.id === id);
    if (draft) return { name: draft.name, config: draft.config, tco: draft.tco || undefined };
    for (const set of sets) {
      const estimate = set.estimates.find(e => e.estimate_id === id);
      if (estimate) {
        return {
          name: estimate.name,
          estimate_id: estimate.estimate_id,
          config: estimate.config,
          tco: tcoOverridesOf(estimate),
        };
      }
    }
    return null;
  }, [drafts, sets]);

  /** The scenario set a share can be published from: 2-3 saved scenarios, one set. */
  const shareableSet = useMemo(() => {
    if (selected.length < 2 || !canManage) return null;
    const groups = selected.map(id => sets.find(s => s.estimates.some(e => e.estimate_id === id)));
    if (groups.some(g => !g) || new Set(groups.map(g => g.group_id)).size !== 1) return null;
    return groups[0];
  }, [selected, sets, canManage]);

  const compare = async () => {
    const payload = selected.map(resolve).filter(Boolean);
    if (payload.length < 2) return;
    setComparing(true);
    try {
      const { data } = await axios.post(`${API}/scenarios/compare`, {
        scenarios: payload, include_tco: true,
      });
      setComparison(data);
      setShareSnapshot(null);
    } catch (err) {
      logger.warn('[ScenariosPage] compare failed:', err?.message || err);
      toast.error(t('scenarios.compareFailed'));
    } finally {
      setComparing(false);
    }
  };

  /** Index of the owner-selected scenario within the current comparison. */
  const preferredIndex = useMemo(() => {
    if (shareSnapshot) return shareSnapshot.decision?.preferred_scenario_index ?? null;
    const preferred = shareableSet?.decision?.preferred_estimate_id;
    if (!preferred) return null;
    const index = selected.indexOf(preferred);
    return index === -1 ? null : index;
  }, [shareSnapshot, shareableSet, selected]);

  const exportBoardPack = async () => {
    if (!user) {
      // Funnel step: anonymous comparison -> authentication intent. Recorded
      // BEFORE the redirect so the drop-off between intent and account
      // creation/sign-in is measurable.
      trackProductEvent('board_pack_auth_prompted', {
        scenario_count: comparison?.scenarios?.length ?? drafts.length,
        has_selected_scenario: preferredIndex != null,
        source: 'workspace',
      });
      toast.info(t('scenarios.boardPackHint'));
      navigate(localePath('/auth'), { state: { from: location.pathname } });
      return;
    }
    if (!comparison) return;

    // Every figure comes from the backend: a frozen share snapshot when one
    // exists, otherwise the server-recalculated comparison.
    let payload = shareSnapshot;
    if (!payload) {
      payload = {
        scenarios: comparison.scenarios,
        differences: comparison.differences,
        engine_version: comparison.engine_version,
        tco_model_version: comparison.tco_model_version,
        generated_at: new Date().toISOString(),
        decision: {
          organisation: shareableSet?.decision?.organisation || null,
          title: shareableSet?.decision?.title || null,
          context: shareableSet?.decision?.context || null,
          note: shareableSet?.decision?.note || null,
          preferred_scenario_index: preferredIndex,
        },
      };
      try {
        const { data } = await axios.post(`${API}/estimates/sensitivity`, {
          config: comparison.scenarios[0].config,
        });
        payload.sensitivity_top = data.variables.slice(0, 3);
      } catch (err) {
        logger.debug('[ScenariosPage] drivers unavailable for the board pack:', err?.message || err);
      }
    }

    try {
      generateBoardPackPDF({ payload, t });
      trackProductEvent('board_pack_generated', {
        scenario_count: payload.scenarios.length,
        has_selected_scenario: payload.decision?.preferred_scenario_index != null,
        source: 'workspace',
        // Hashed into `group_ref` server-side (never stored raw) so Board Pack
        // depth per scenario set is measurable without an owner identifier.
        ...(shareableSet?.group_id ? { group_id: shareableSet.group_id } : {}),
      });
      toast.success(t('scenarios.boardPackDone'));
    } catch (err) {
      logger.warn('[ScenariosPage] board pack export failed:', err?.message || err);
      toast.error(<span data-testid="board-pack-export-error">{t('scenarios.boardPackFailed')}</span>);
    }
  };

  const saveDraftsAsSet = async () => {
    setSavingSet(true);
    try {
      const [first, ...rest] = drafts;
      const { data } = await axios.post(`${API}/estimates`, {
        name: first.name, config: first.config, tco: first.tco || undefined,
      }, { withCredentials: true });
      for (const draft of rest) {
        await axios.post(`${API}/estimates`, {
          name: draft.name, config: draft.config, tco: draft.tco || undefined,
          scenario_group_id: data.scenario_group_id,
        }, { withCredentials: true });
      }
      drafts.forEach(draft => removeDraft(draft.id));
      setDrafts(readDrafts());
      setSelected([]);
      resetComparison();
      toast.success(t('scenarios.savedAsSet'));
      fetchSets();
    } catch (err) {
      logger.warn('[ScenariosPage] save as set failed:', err?.message || err);
      toast.error(t('scenarios.saveAsSetFailed'));
    } finally {
      setSavingSet(false);
    }
  };

  const duplicate = async (estimate) => {
    try {
      await axios.post(`${API}/estimates/${estimate.estimate_id}/duplicate`, {}, { withCredentials: true });
      toast.success(t('scenarios.duplicated'));
      fetchSets();
    } catch (err) {
      logger.warn('[ScenariosPage] duplicate failed:', err?.message || err);
      toast.error(t('scenarios.duplicateFailed'));
    }
  };

  const submitRename = async () => {
    if (!renameValue.trim() || !renaming) return;
    try {
      await axios.patch(`${API}/estimates/${renaming.estimate_id}`, { name: renameValue.trim() },
        { withCredentials: true });
      toast.success(t('scenarios.renamed'));
      setRenaming(null);
      fetchSets();
    } catch (err) {
      logger.warn('[ScenariosPage] rename failed:', err?.message || err);
      toast.error(t('scenarios.renameFailed'));
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await axios.delete(`${API}/estimates/${pendingDelete.estimate_id}`, { withCredentials: true });
      setSelected(prev => prev.filter(id => id !== pendingDelete.estimate_id));
      resetComparison();
      toast.success(t('scenarios.deleted'));
      setPendingDelete(null);
      fetchSets();
    } catch (err) {
      logger.warn('[ScenariosPage] delete failed:', err?.message || err);
      toast.error(t('scenarios.deleteFailed'));
    }
  };

  const dropDraft = (id) => {
    removeDraft(id);
    setDrafts(readDrafts());
    setSelected(prev => prev.filter(s => s !== id));
    resetComparison();
  };

  const hasScenarios = drafts.length > 0 || sets.length > 0;

  const compareAction = (
    <Button
      onClick={compare}
      disabled={selected.length < 2 || comparing}
      className="h-9 bg-[#527F19] hover:bg-[#3F6114] text-white rounded-md disabled:opacity-40"
      data-testid="scenario-compare-btn"
    >
      {comparing
        ? <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
        : <GitCompareArrows className="w-4 h-4 mr-2" aria-hidden="true" />}
      {t('scenarios.compare')}
    </Button>
  );

  return (
    <AppShell title={t('scenarios.title')} subtitle={t('scenarios.subtitle')} actions={compareAction}>
      <div className="workspace-main-width space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#66707A]" data-testid="scenario-select-hint">
            {selected.length < 2 ? t('scenarios.selectHint') : t('scenarios.compareLimit')}
          </p>
            <ActionLink to="/calculator"
              variant="outline"
              className="h-9 border-[#DDE1E5] bg-white text-[#3A4249] hover:bg-[#F5F6F7] rounded-md"
              data-testid="scenario-new-btn"
            >
              <Calculator className="w-4 h-4 mr-2" aria-hidden="true" /> {t('scenarios.goToCalculator')}
            </ActionLink>
        </div>

        {!user && (
          <div className="border border-[#2867B2]/30 bg-[#2867B2]/[0.06] rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3"
               data-testid="scenario-signin-banner">
            <p className="text-sm text-[#1F4E86]">{t('scenarios.signInHint')}</p>
              <ActionLink to="/auth" variant="outline"
                      data-testid="scenario-signin-btn">
                {t('scenarios.signInToSave')}
              </ActionLink>
          </div>
        )}

        {drafts.length > 0 && (
          <div className="border border-[#DDE1E5] rounded-lg bg-white" data-testid="scenario-drafts">
            <div className="px-5 py-3.5 border-b border-[#DDE1E5] flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-sm font-medium text-[#171A1D]">{t('scenarios.draftsTitle')}</h2>
                <p className="mt-0.5 text-xs text-[#66707A]">{t('scenarios.draftsIntro')}</p>
              </div>
              {canManage && (
                <Button
                  variant="outline"
                  onClick={saveDraftsAsSet}
                  disabled={savingSet}
                  className="h-8 border-[#DDE1E5] text-[#3A4249] hover:bg-[#F5F6F7] rounded-md text-xs shrink-0"
                  data-testid="scenario-save-set-btn"
                  title={t('scenarios.saveAsSetHint')}
                >
                  {savingSet
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden="true" />
                    : <Save className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />}
                  {t('scenarios.saveAsSet')}
                </Button>
              )}
            </div>
            <ul x-excluded="true" className="divide-y divide-[#DDE1E5]">
              {drafts.map(draft => (
                <li
                  key={draft.id}
                  className={`px-5 py-3 flex items-center gap-3 ${selected.includes(draft.id) ? 'bg-[#F1F8E8]' : ''}`}
                  data-testid={`scenario-draft-${draft.id}`}
                >
                  <label className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.includes(draft.id)}
                      onChange={() => toggle(draft.id)}
                      className="w-4 h-4 accent-[#527F19] shrink-0"
                      data-testid={`scenario-draft-select-${draft.id}`}
                      aria-label={`${t('scenarios.select')}: ${draft.name}`}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm text-[#171A1D] truncate">{draft.name}</span>
                      <span className="block text-[10px] uppercase tracking-[0.08em] text-[#8A929B]"
                            style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {t('scenarios.draft')}
                      </span>
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => dropDraft(draft.id)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-[6px] border border-[#DDE1E5] text-[#66707A] hover:text-[#B42318] transition-colors duration-150 shrink-0"
                    data-testid={`scenario-draft-remove-${draft.id}`}
                    aria-label={t('scenarios.removeDraft')}
                  >
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 py-10 text-sm text-[#66707A]" data-testid="scenarios-loading">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('scenarios.loading')}
          </div>
        )}

        {!loading && sets.length > 0 && (
          <div className="space-y-4" data-testid="scenario-sets">
            {sets.map(set => (
              <ScenarioSetCard
                key={set.group_id}
                set={set}
                selectedIds={selected}
                canManage={canManage}
                onToggle={toggle}
                onDuplicate={duplicate}
                onRename={(estimate) => { setRenaming(estimate); setRenameValue(estimate.name); }}
                onDelete={setPendingDelete}
                shareCount={shares.filter(s => s.scenario_group_id === set.group_id && s.status === 'active').length}
              >
                {canManage && <DecisionContextCard set={set} onSaved={fetchSets} />}
              </ScenarioSetCard>
            ))}
          </div>
        )}

        {!loading && !hasScenarios && (
          <EmptyState testId="scenarios-empty" title={t('scenarios.emptyTitle')} description={t('scenarios.emptyBody')} icon={Layers}>
              <ActionLink to="/calculator" className="mt-5"
                      data-testid="scenarios-empty-cta">
                <Calculator className="w-4 h-4 mr-2" aria-hidden="true" /> {t('scenarios.goToCalculator')}
              </ActionLink>
          </EmptyState>
        )}

        {comparing && (
          <div className="flex items-center gap-2 text-sm text-[#66707A]" data-testid="scenarios-comparing">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('scenarios.comparing')}
          </div>
        )}

        {comparison && !comparing && (
          <>
            <div className="flex flex-wrap items-center gap-3 border border-[#DDE1E5] rounded-lg bg-white px-4 py-3"
                 data-testid="decision-actions">
              <Button
                variant="secondary"
                ref={shareTriggerRef}
                onClick={() => setShareOpen(true)}
                disabled={!shareableSet}
                className="h-9 bg-[#171A1D] hover:bg-[#3A4249] text-white rounded-md disabled:opacity-40"
                data-testid="share-comparison-btn"
              >
                <Share2 className="w-4 h-4 mr-2" aria-hidden="true" /> {t('scenarios.share')}
              </Button>
              <Button
                variant="outline"
                onClick={exportBoardPack}
                className="h-9 border-[#DDE1E5] text-[#3A4249] hover:bg-[#F5F6F7] rounded-md"
                data-testid="board-pack-btn"
              >
                <FileText className="w-4 h-4 mr-2" aria-hidden="true" />
                {user ? t('scenarios.boardPack') : t('scenarios.boardPackAnon')}
              </Button>
              {!shareableSet && (
                <p className="text-xs text-[#8A929B] flex-1 min-w-[200px]" data-testid="share-requires-set">
                  {t('scenarios.shareRequiresSet')}
                </p>
              )}
              {shareSnapshot && (
                <p className="text-xs text-[#527F19] flex-1 min-w-[200px]" data-testid="share-snapshot-note">
                  {t('scenarios.version', { version: shareSnapshot.version })}
                </p>
              )}
            </div>

            <ScenarioComparison comparison={comparison} preferredIndex={preferredIndex} />
          </>
        )}
      </div>

      {shareableSet && (
        <ShareComparisonDialog
          open={shareOpen}
          triggerRef={shareTriggerRef}
          onOpenChange={setShareOpen}
          groupId={shareableSet.group_id}
          estimateIds={selected}
          shares={shares}
          onChanged={fetchSets}
          onSnapshot={(snapshot) => { setShareSnapshot(snapshot); setComparison(snapshot); }}
        />
      )}

      <Dialog open={Boolean(renaming)} onOpenChange={(open) => { if (!open) setRenaming(null); }}>
        <DialogContent className="bg-white border-[#DDE1E5]" aria-describedby={undefined} data-testid="scenario-rename-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#171A1D]">{t('scenarios.renameTitle')}</DialogTitle>
          </DialogHeader>
          <label className="block text-xs text-[#66707A]" htmlFor="scenario-rename-input">
            {t('scenarios.renameLabel')}
          </label>
          <Input
            id="scenario-rename-input"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); }}
            className="border-[#DDE1E5]"
            data-testid="scenario-rename-input"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRenaming(null)}
              className="border-[#DDE1E5] text-[#3A4249]"
              data-testid="scenario-rename-cancel"
            >
              {t('assumptions.cancel')}
            </Button>
            <Button
              onClick={submitRename}
              disabled={!renameValue.trim()}
              className="bg-[#527F19] hover:bg-[#3F6114] text-white"
              data-testid="scenario-rename-save"
            >
              {t('scenarios.rename')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <AlertDialogContent className="bg-white border-[#DDE1E5]" data-testid="scenario-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#171A1D]">{t('scenarios.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-[#66707A]">
              {t('scenarios.deleteBody', { name: pendingDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#DDE1E5] text-[#3A4249] hover:bg-[#F5F6F7]"
                               data-testid="scenario-delete-cancel">
              {t('scenarios.deleteCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-[#B42318] hover:bg-[#8F1C13] text-white"
              data-testid="scenario-delete-confirm"
            >
              {t('scenarios.deleteConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
